import { App, Button } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { Maybe } from 'shared/types';
import { DEFAULT_LOGOUT_TOAST_TIMEOUT, TOAST_KEY, TIMER_OFFSET } from '../const';
import { getFormattedTime } from './getFormattedTime';
import { getLogoutTime } from './getLogoutTime';

type UseLogoutWarningToastReturnValue = {
  showToast: (onClickButton: () => void, onEnd: () => void) => Maybe<NodeJS.Timer>;
  destroyToast: () => void;
};

export const useLogoutWarningToast = (): UseLogoutWarningToastReturnValue => {
  const { t } = useTranslation();

  const { notification: toast } = App.useApp();

  const onClickWrapper = (callback: () => void): void => {
    callback();
    toast.destroy(TOAST_KEY);
  };

  const startToastTimer = (onClickButton: () => void, onEnd: () => void): Maybe<NodeJS.Timer> => {
    return setInterval(() => {
      const logoutTime = getLogoutTime();
      if (!logoutTime) {
        onEnd();
        return;
      }
      const currentTime = dayjs();
      const difference = Math.floor(logoutTime.diff(currentTime) / 1000);

      if (difference > (DEFAULT_LOGOUT_TOAST_TIMEOUT + TIMER_OFFSET) / 1000) {
        onClickWrapper(onClickButton);
        return;
      }

      if (difference <= 0) {
        onEnd();
        return;
      }
      toast.warning({
        key: TOAST_KEY,
        message: t('Auth:LogoutToast.content.message', { remainingTime: getFormattedTime(difference) }),
        duration: 0,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => onClickWrapper(onClickButton)}
            data-testid="logout-stay-in-button"
            style={{ marginRight: '10px' }}
          >
            {t('Auth:LogoutToast.content.button.text')}
          </Button>
        ),
        closable: false,
      });
    }, 1000);
  };

  const destroyToast = (): void => toast.destroy(TOAST_KEY);

  return { showToast: startToastTimer, destroyToast };
};
